import React, { useState } from "react";
import { toast } from "sonner";
import {
  postPipChargerPriority,
  postPipMaxChargingCurrent,
  postPipMaxUtilityCurrent,
  postPipOutputPriority,
} from "@client/api/pip";
import { t } from "@client/lib/dictionary";
import { showWriteResult } from "@client/lib/verifyToast";
import { useUserStore } from "@client/store/user";
import type { PipSettings } from "@client/types/pip";
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/components/ui/select";

interface Props {
  slug: string;
  settings: PipSettings | null | undefined;
}

const OUTPUT_PRIORITY_TO_CODE: Record<string, "00" | "01" | "02"> = {
  UTI: "00",
  SOL: "01",
  SBU: "02",
};

const CHARGER_PRIORITY_TO_CODE: Record<string, "00" | "01" | "02" | "03"> = {
  UTI: "00",
  SOL: "01",
  SOL_AND_UTI: "02",
  SOL_ONLY: "03",
};

export const PipControls: React.FC<Props> = ({ slug, settings }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  const [busy, setBusy] = useState(false);

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.pip.detail.controls}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{t.pip.controls.adminRequired}</CardContent>
      </Card>
    );
  }

  const currentOutputCode = settings ? OUTPUT_PRIORITY_TO_CODE[settings.outputPriority] : undefined;
  const currentChargerCode = settings ? CHARGER_PRIORITY_TO_CODE[settings.chargerPriority] : undefined;

  const sendOutputPriority = async (value: "00" | "01" | "02") => {
    setBusy(true);
    try {
      const res = await postPipOutputPriority(slug, value);
      showWriteResult(res.responseObject, "Priorita výstupu");
    } catch (err) {
      toast.error(String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err));
    } finally {
      setBusy(false);
    }
  };

  const sendChargerPriority = async (value: "00" | "01" | "02" | "03") => {
    setBusy(true);
    try {
      const res = await postPipChargerPriority(slug, value);
      showWriteResult(res.responseObject, "Priorita nabíjení");
    } catch (err) {
      toast.error(String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err));
    } finally {
      setBusy(false);
    }
  };

  const sendMaxChg = async (amps: number) => {
    setBusy(true);
    try {
      const res = await postPipMaxChargingCurrent(slug, amps);
      showWriteResult(res.responseObject, "Max. nabíjecí proud");
    } catch (err) {
      toast.error(String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err));
    } finally {
      setBusy(false);
    }
  };

  const sendMaxUtil = async (amps: number) => {
    setBusy(true);
    try {
      const res = await postPipMaxUtilityCurrent(slug, amps);
      showWriteResult(res.responseObject, "Max. proud ze sítě");
    } catch (err) {
      toast.error(String((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.pip.detail.controls}</CardTitle>
        <p className="text-xs text-amber-600">⚠ {t.pip.controls.readOnlyWarning}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm font-medium mb-1">{t.pip.controls.outputPriority}</div>
          <Select
            value={currentOutputCode ?? ""}
            onValueChange={(v) => sendOutputPriority(v as "00" | "01" | "02")}
            disabled={busy}
          >
            <SelectTrigger>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="00">{t.pip.controls.outputPriorityOptions["00"]}</SelectItem>
              <SelectItem value="01">{t.pip.controls.outputPriorityOptions["01"]}</SelectItem>
              <SelectItem value="02">{t.pip.controls.outputPriorityOptions["02"]}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">{t.pip.controls.chargerPriority}</div>
          <Select
            value={currentChargerCode ?? ""}
            onValueChange={(v) => sendChargerPriority(v as "00" | "01" | "02" | "03")}
            disabled={busy}
          >
            <SelectTrigger>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="00">{t.pip.controls.chargerPriorityOptions["00"]}</SelectItem>
              <SelectItem value="01">{t.pip.controls.chargerPriorityOptions["01"]}</SelectItem>
              <SelectItem value="02">{t.pip.controls.chargerPriorityOptions["02"]}</SelectItem>
              <SelectItem value="03">{t.pip.controls.chargerPriorityOptions["03"]}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {settings && settings.allowedTotalChargingCurrentsA.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-1">
              {t.pip.controls.maxChargingCurrent} (aktuálně {settings.maxTotalChargingCurrentA} A)
            </div>
            <div className="flex flex-wrap gap-1">
              {settings.allowedTotalChargingCurrentsA.map((a) => (
                <Button
                  key={a}
                  size="sm"
                  variant={a === settings.maxTotalChargingCurrentA ? "default" : "outline"}
                  disabled={busy}
                  onClick={() => sendMaxChg(a)}
                >
                  {a} A
                </Button>
              ))}
            </div>
          </div>
        )}

        {settings && settings.allowedUtilityChargingCurrentsA.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-1">
              {t.pip.controls.maxUtilityCurrent} (aktuálně {settings.maxAcChargingCurrentA} A)
            </div>
            <div className="flex flex-wrap gap-1">
              {settings.allowedUtilityChargingCurrentsA.map((a) => (
                <Button
                  key={a}
                  size="sm"
                  variant={a === settings.maxAcChargingCurrentA ? "default" : "outline"}
                  disabled={busy}
                  onClick={() => sendMaxUtil(a)}
                >
                  {a} A
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
